import {Injectable} from '@angular/core';
import {AuthService} from '@shared/services/auth.service';
import {PermissionOptions} from '@users/models/user.interface';
import {hasBlocker} from '@vat/engine/issues';
import type {
    LandlordTaxProfile,
    VatAdjustment,
    VatEngineResult,
    VatLine,
    VatReturn,
    VatReturnChecklistEntry,
    VatReturnExport,
    VatReturnStatus
} from '@vat/engine/types';
import firebase from 'firebase/compat/app';
import {firstValueFrom} from 'rxjs';
import {VAT_COMMON_COPY, VAT_CORRECTION_COPY, VAT_WORKFLOW_COPY} from '../vat-copy';
import {BuildDraftVatReturnParams, buildDraftVatReturn, buildVatReturnID, normalizeUndefinedToNull, normalizeVatReturnDates} from './vat-return.factory';
import {
    buildVatActivity,
    canTransition,
    checklistFieldPath,
    chunkDocID,
    chunkLines,
    flattenChunks,
    nextVersion,
    periodLabel,
    releasePatch,
    revokePatch,
    VAT_ACTIVITY_ICONS,
    VatLinesChunk
} from './vat-return-workflow.util';
import {VatReturnLinesChunk, VatReturnsService} from './vat-returns.service';
import FieldValue = firebase.firestore.FieldValue;
import WriteBatch = firebase.firestore.WriteBatch;
import DocumentReference = firebase.firestore.DocumentReference;

export interface WorkflowContext {
    landlordID: string;
    landlordsName: string | null;
    year: number;
    periodKey: string;
}

export interface ReleaseParams {
    returnID: string;
    draft: BuildDraftVatReturnParams;
    context: WorkflowContext;
    result: VatEngineResult;
    profile: LandlordTaxProfile;
    bestaetigung: string;
}

export interface Nachtrag17Params {
    source: VatReturn;
    sourceContext: WorkflowContext;
    targetDraft: BuildDraftVatReturnParams;
    targetContext: WorkflowContext;
    adjustments: VatAdjustment[];
    liveInputHash: string;
}

/**
 * Status-Workflow der Voranmeldung (Spec 8): ENTWURF → GEPRUEFT → EXPORTIERT → BERICHTIGT.
 * Jeder Übergang liest zuerst `get({source: 'server'})`, prüft BOOKINGS_WRITE und `canTransition`,
 * und schreibt Dokument, lines-Chunks und Activities (lokal + Kunden-Feed) in EINEM Batch.
 * Schreibt ausschließlich `vatReturns/*` (+ Subcollections) und den Kunden-Feed `activities`.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatReturnWorkflowService {

    constructor(private authService: AuthService, private vatReturnsService: VatReturnsService) {
    }

    // ------------------------------------------------------------------
    // Lesen
    // ------------------------------------------------------------------

    async getServer(returnID: string): Promise<VatReturn | null> {
        const snapshot = await firstValueFrom(this.vatReturnsService.doc(returnID).get({source: 'server'}));
        return snapshot.exists ? normalizeVatReturnDates({...(snapshot.data() as VatReturn), id: snapshot.id}) : null;
    }

    async loadSnapshotLines(returnID: string): Promise<VatLine[]> {
        const snapshot = await firstValueFrom(this.vatReturnsService.linesCollection(returnID).get());
        const chunks: VatLinesChunk[] = snapshot.docs.map(doc => {
            const data = doc.data() as VatReturnLinesChunk;
            return {index: data.index ?? 0, lines: (data.lines ?? []) as VatLine[]};
        });
        return flattenChunks(chunks);
    }

    // ------------------------------------------------------------------
    // ENTWURF
    // ------------------------------------------------------------------

    /** Das Dokument entsteht beim ersten Schreibvorgang (Spec 3.4) – Ansehen schreibt nichts. */
    async ensureDraft(draft: BuildDraftVatReturnParams): Promise<VatReturn> {
        this.assertWrite();
        const id = buildVatReturnID(draft.landlordID, draft.year, draft.periodKey, draft.version ?? 1);
        const existing = await this.getServer(id);
        if (existing) {
            return existing;
        }
        const created = buildDraftVatReturn({...draft, uid: this.actor().uid, now: new Date()});
        const batch = this.vatReturnsService.batch();
        batch.set(this.vatReturnsService.doc(id).ref, normalizeUndefinedToNull(created));
        await batch.commit();
        return created;
    }

    async saveAdjustments(returnID: string, draft: BuildDraftVatReturnParams, adjustments: VatAdjustment[]): Promise<void> {
        const doc = await this.ensureDraft(draft);
        this.assertStatus(doc, 'ENTWURF', VAT_WORKFLOW_COPY.errors.notDraft);
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({adjustments}));
        await batch.commit();
    }

    async setChecklistEntry(returnID: string, draft: BuildDraftVatReturnParams, issueID: string, entry: VatReturnChecklistEntry | null): Promise<void> {
        const doc = await this.ensureDraft(draft);
        this.assertStatus(doc, 'ENTWURF', VAT_WORKFLOW_COPY.errors.notDraft);
        const fieldPath = checklistFieldPath(issueID);
        const batch = this.vatReturnsService.batch();
        if (fieldPath) {
            batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({[fieldPath]: entry ?? FieldValue.delete()}));
        } else {
            // Schlüssel mit Sonderzeichen: ganze Map schreiben (kein dotted key möglich)
            const checklist = {...(doc.checklist ?? {})};
            if (entry) {
                checklist[issueID] = entry;
            } else {
                delete checklist[issueID];
            }
            batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({checklist}));
        }
        await batch.commit();
    }

    /** „Verwerfen“ statt Löschen (Spec 8): Prüfliste und Korrekturen zurücksetzen. */
    async discardDraft(returnID: string, context: WorkflowContext): Promise<void> {
        this.assertWrite();
        const doc = await this.requireServer(returnID);
        this.assertStatus(doc, 'ENTWURF', VAT_WORKFLOW_COPY.errors.notDraft);
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({checklist: {}, adjustments: []}));
        this.appendActivity(batch, returnID, context, VAT_ACTIVITY_ICONS.discarded, VAT_WORKFLOW_COPY.activities.discarded(periodLabel(context.year, context.periodKey)));
        await batch.commit();
    }

    // ------------------------------------------------------------------
    // Freigabe / Rücknahme
    // ------------------------------------------------------------------

    async release(params: ReleaseParams): Promise<void> {
        this.assertWrite();
        if (hasBlocker(params.result.issues)) {
            throw new Error(VAT_WORKFLOW_COPY.errors.blockers);
        }
        const existing = await this.getServer(params.returnID);
        if (existing) {
            this.assertTransition(existing.status, 'GEPRUEFT');
        }
        const actor = this.actor();
        const now = new Date();
        const patch = releasePatch({result: params.result, profile: params.profile, uid: actor.uid, displayName: actor.displayName, now, bestaetigung: params.bestaetigung});

        const batch = this.vatReturnsService.batch();
        const ref = this.vatReturnsService.doc(params.returnID).ref;
        if (existing) {
            batch.update(ref, this.audit(patch, now));
        } else {
            batch.set(ref, normalizeUndefinedToNull({...buildDraftVatReturn({...params.draft, uid: actor.uid, now}), ...patch, updatedAt: now, updatedBy: actor.uid}));
        }

        // lines-Snapshot: neue Chunks setzen, überzählige alte löschen (A4)
        const chunks = chunkLines(params.result.lines);
        const existingChunks = await firstValueFrom(this.vatReturnsService.linesCollection(params.returnID).get());
        const newIDs = new Set(chunks.map(chunk => chunkDocID(chunk.index)));
        existingChunks.docs.filter(doc => !newIDs.has(doc.id)).forEach(doc => batch.delete(doc.ref));
        chunks.forEach(chunk => batch.set(this.vatReturnsService.linesCollection(params.returnID).doc(chunkDocID(chunk.index)).ref, normalizeUndefinedToNull({index: chunk.index, lines: chunk.lines})));

        this.appendActivity(batch, params.returnID, params.context, VAT_ACTIVITY_ICONS.released, VAT_WORKFLOW_COPY.activities.released(periodLabel(params.context.year, params.context.periodKey)), now);
        await batch.commit();
    }

    async revoke(returnID: string, context: WorkflowContext, reason: string): Promise<void> {
        this.assertWrite();
        const trimmed = (reason ?? '').trim();
        if (!trimmed) {
            throw new Error(VAT_WORKFLOW_COPY.errors.reasonRequired);
        }
        const doc = await this.requireServer(returnID);
        this.assertTransition(doc.status, 'ENTWURF');
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit(revokePatch()));
        this.appendActivity(batch, returnID, context, VAT_ACTIVITY_ICONS.revoked, VAT_WORKFLOW_COPY.activities.revoked(periodLabel(context.year, context.periodKey), trimmed));
        await batch.commit();
    }

    // ------------------------------------------------------------------
    // Export / Übermittlung (Plan 4 ruft markExported)
    // ------------------------------------------------------------------

    async markExported(returnID: string, context: WorkflowContext, entry: VatReturnExport): Promise<void> {
        this.assertWrite();
        const doc = await this.requireServer(returnID);
        const patch: Partial<VatReturn> = {exports: [...(doc.exports ?? []), entry]};
        if (doc.status === 'GEPRUEFT') {
            patch.status = 'EXPORTIERT';
        } else if (doc.status !== 'EXPORTIERT') {
            throw new Error(VAT_WORKFLOW_COPY.errors.transition(doc.status, 'EXPORTIERT'));
        }
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit(patch));
        this.appendActivity(batch, returnID, context, VAT_ACTIVITY_ICONS.exported, VAT_WORKFLOW_COPY.activities.exported(periodLabel(context.year, context.periodKey), entry.type));
        await batch.commit();
    }

    async markUebermittelt(returnID: string, context: WorkflowContext, am: Date, transferticket: string | null): Promise<void> {
        this.assertWrite();
        const doc = await this.requireServer(returnID);
        this.assertStatus(doc, 'EXPORTIERT', VAT_WORKFLOW_COPY.errors.transition(doc.status, 'EXPORTIERT'));
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({uebermittelt: {am, von: this.actor().uid, transferticket: transferticket?.trim() || null}}));
        this.appendActivity(batch, returnID, context, VAT_ACTIVITY_ICONS.transmitted, VAT_WORKFLOW_COPY.activities.transmitted(periodLabel(context.year, context.periodKey)));
        await batch.commit();
    }

    /**
     * Weg 3 der Änderungs-Erkennung: Der Live-Hash weicht ab, aber kein Zeilen-Delta (nur Stammdaten wie
     * Steuerprofil, Vorsteuer-Schlüssel, Flächen). Statt eines § 17-Nachtrags wird der aktuelle Stand als
     * bestätigt vermerkt (`bestaetigterInputHash`), Status und Snapshot bleiben unverändert.
     */
    async acknowledgeLiveHash(returnID: string, context: WorkflowContext, liveInputHash: string): Promise<void> {
        this.assertWrite();
        const doc = await this.requireServer(returnID);
        if (doc.status !== 'GEPRUEFT' && doc.status !== 'EXPORTIERT') {
            throw new Error(VAT_WORKFLOW_COPY.errors.transition(doc.status, doc.status));
        }
        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(returnID).ref, this.audit({bestaetigterInputHash: liveInputHash}));
        this.appendActivity(batch, returnID, context, VAT_ACTIVITY_ICONS.acknowledged, VAT_WORKFLOW_COPY.activities.acknowledged(periodLabel(context.year, context.periodKey)));
        await batch.commit();
    }

    // ------------------------------------------------------------------
    // Berichtigung
    // ------------------------------------------------------------------

    async createCorrection(
        original: VatReturn,
        context: WorkflowContext,
        draftBase: Omit<BuildDraftVatReturnParams, 'version' | 'korrekturVonID'>,
        allVersions: VatReturn[]
    ): Promise<string> {
        this.assertWrite();
        const server = await this.requireServer(original.id);
        this.assertTransition(server.status, 'BERICHTIGT');
        const actor = this.actor();
        const now = new Date();
        const version = nextVersion(allVersions, original.periodKey);
        const correction = buildDraftVatReturn({...draftBase, version, korrekturVonID: original.id, uid: actor.uid, now});

        const batch = this.vatReturnsService.batch();
        batch.set(this.vatReturnsService.doc(correction.id).ref, normalizeUndefinedToNull(correction));
        batch.update(this.vatReturnsService.doc(original.id).ref, this.audit({korrigiertDurchID: correction.id, vorherigerStatus: server.status, status: 'BERICHTIGT'}, now));
        this.appendActivity(batch, original.id, context, VAT_ACTIVITY_ICONS.correction, VAT_WORKFLOW_COPY.activities.correctionCreated(periodLabel(context.year, context.periodKey), version), now);
        await batch.commit();
        return correction.id;
    }

    /** A3: nur solange die Berichtigung ENTWURF ist; Zeiger am Original per FieldValue.delete(), kein removeEmpty. */
    async revertCorrection(correction: VatReturn, context: WorkflowContext): Promise<void> {
        this.assertWrite();
        if (!correction.korrekturVonID) {
            throw new Error(VAT_WORKFLOW_COPY.errors.notCorrection);
        }
        const server = await this.requireServer(correction.id);
        this.assertStatus(server, 'ENTWURF', VAT_WORKFLOW_COPY.errors.notDraft);
        const original = await this.requireServer(correction.korrekturVonID);
        const restored: VatReturnStatus = original.vorherigerStatus ?? 'GEPRUEFT';
        this.assertTransition(original.status, restored);

        const batch = this.vatReturnsService.batch();
        batch.update(this.vatReturnsService.doc(original.id).ref, this.audit({
            status: restored,
            korrigiertDurchID: FieldValue.delete() as unknown as string,
            vorherigerStatus: FieldValue.delete() as unknown as VatReturnStatus,
        }));
        this.appendActivity(batch, original.id, context, VAT_ACTIVITY_ICONS.reverted, VAT_WORKFLOW_COPY.activities.correctionReverted(periodLabel(context.year, context.periodKey), correction.version));
        await batch.commit();
    }

    // ------------------------------------------------------------------
    // § 17-Nachtrag im laufenden Zeitraum
    // ------------------------------------------------------------------

    async recordNachtrag17(params: Nachtrag17Params): Promise<string> {
        this.assertWrite();
        if (!params.adjustments.length) {
            throw new Error(VAT_CORRECTION_COPY.noDelta);
        }
        const targetID = buildVatReturnID(params.targetDraft.landlordID, params.targetDraft.year, params.targetDraft.periodKey, params.targetDraft.version ?? 1);
        const [source, target] = await Promise.all([this.requireServer(params.source.id), this.getServer(targetID)]);
        if (target && target.status !== 'ENTWURF') {
            throw new Error(VAT_CORRECTION_COPY.noOpenPeriod);   // A5: Ziel ist nicht mehr ENTWURF → Berichtigung wählen
        }
        const actor = this.actor();
        const now = new Date();
        const batch = this.vatReturnsService.batch();
        const targetRef = this.vatReturnsService.doc(targetID).ref;
        if (target) {
            batch.update(targetRef, this.audit({adjustments: [...(target.adjustments ?? []), ...params.adjustments]}, now));
        } else {
            batch.set(targetRef, normalizeUndefinedToNull({...buildDraftVatReturn({...params.targetDraft, uid: actor.uid, now}), adjustments: params.adjustments}));
        }
        batch.update(this.vatReturnsService.doc(source.id).ref, this.audit({bestaetigterInputHash: params.liveInputHash}, now));
        const sourceLabel = periodLabel(params.sourceContext.year, params.sourceContext.periodKey);
        this.appendActivity(batch, targetID, params.targetContext, VAT_ACTIVITY_ICONS.nachtrag,
            VAT_WORKFLOW_COPY.activities.nachtrag(periodLabel(params.targetContext.year, params.targetContext.periodKey), sourceLabel, params.adjustments.length), now);
        await batch.commit();
        return targetID;
    }

    // ------------------------------------------------------------------
    // Intern
    // ------------------------------------------------------------------

    private actor(): { uid: string; displayName: string } {
        return {uid: this.authService.authUser?.uid ?? '', displayName: this.authService.authUser?.displayName ?? this.authService.authUser?.email ?? 'Unbekannt'};
    }

    private assertWrite(): void {
        if (!this.authService.hasPermission(PermissionOptions.BOOKINGS_WRITE)) {
            throw new Error(VAT_COMMON_COPY.noWritePermission);
        }
    }

    private assertTransition(from: VatReturnStatus, to: VatReturnStatus): void {
        if (!canTransition(from, to)) {
            throw new Error(VAT_WORKFLOW_COPY.errors.transition(from, to));
        }
    }

    private assertStatus(doc: VatReturn, expected: VatReturnStatus, message: string): void {
        if (doc.status !== expected) {
            throw new Error(message);
        }
    }

    private async requireServer(returnID: string): Promise<VatReturn> {
        const doc = await this.getServer(returnID);
        if (!doc) {
            throw new Error(VAT_WORKFLOW_COPY.errors.notFound);
        }
        return doc;
    }

    /** Audit-Felder ergänzen und undefined → null (FieldValue bleibt unangetastet, kein removeEmpty). */
    private audit(patch: Record<string, unknown>, now: Date = new Date()): Record<string, unknown> {
        return normalizeUndefinedToNull({...patch, updatedAt: now, updatedBy: this.actor().uid});
    }

    private activityRefs(returnID: string, activityID: string): [DocumentReference, DocumentReference] {
        const local = this.authService.afs.doc(this.vatReturnsService.buildFirestorePath(`vatReturns/${returnID}/activities/${activityID}`)).ref as DocumentReference;
        const feed = this.authService.afs.doc(this.vatReturnsService.buildFirestorePath(`activities/${activityID}`)).ref as DocumentReference;
        return [local, feed];
    }

    private appendActivity(batch: WriteBatch, returnID: string, context: WorkflowContext, icon: string, description: string, now: Date = new Date()): void {
        const actor = this.actor();
        const activityID = this.vatReturnsService.createID();
        const activity = normalizeUndefinedToNull(buildVatActivity({
            id: activityID, uid: actor.uid, displayName: actor.displayName, description, icon, returnID,
            landlordID: context.landlordID, landlordsName: context.landlordsName, customerID: this.authService.customerID ?? null, now,
        }));
        for (const ref of this.activityRefs(returnID, activityID)) {
            batch.set(ref, activity);
        }
    }
}
